import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PriceDisplay } from '@/components/PriceDisplay';
import { durationColor } from '@/lib/badgeColors';
import { useTheme } from '@/contexts/ThemeContext';
import Footer from '@/components/Footer';
import { ArrowLeft, Headphones, Package, Clock, Sun, Moon, Shield, ArrowRight, Star, CheckCircle } from 'lucide-react';

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [category, setCategory] = useState(null);

  useEffect(() => {
    api.get(`/public/categories/slug/${slug}`).then(r => setCategory(r.data)).catch(() => navigate('/'));
  }, [slug, navigate]);

  if (!category) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#1A9DA5] border-t-transparent rounded-full" /></div>;

  const guides = (category.products || []).filter(p => p.type === 'guide');
  const packages = (category.products || []).filter(p => p.type === 'package');
  const featuredProducts = (category.featured_products || []);

  return (
    <div className="min-h-screen" data-testid="category-page">
      <header className="sticky top-0 z-50 border-b glass-effect bg-background/80">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Wayfinder" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-lg font-['Outfit']">Wayfinder</span>
          </Link>
          <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="category-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#081E2B] via-[#0C2D3F] to-[#0F3D4D]" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(26,157,165,0.25) 0%, transparent 50%), radial-gradient(ellipse at 80% 30%, rgba(232,115,90,0.15) 0%, transparent 40%)' }} />
        <div className="absolute top-10 left-[5%] w-72 h-72 rounded-full bg-[#1A9DA5]/8 blur-[100px] animate-pulse" />
        <div className="absolute bottom-10 right-[8%] w-60 h-60 rounded-full bg-[#E8735A]/8 blur-[80px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full border border-white/[0.03]" />

        <div className="relative max-w-5xl mx-auto px-4 py-20 md:py-28">
          <Button variant="ghost" className="mb-6 text-white/60 hover:text-white hover:bg-white/10" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-1" />Back to Store
          </Button>
          <div className="text-white max-w-3xl">
            <Badge className="bg-[#1A9DA5]/20 text-[#1A9DA5] border-[#1A9DA5]/30 mb-4">{category.name}</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] mb-4 leading-tight">{category.longer_title || category.name}</h1>
            {category.subtitle && <p className="text-xl text-white/60 mb-6">{category.subtitle}</p>}
            {category.short_description && <p className="text-base text-white/50 max-w-2xl leading-relaxed">{category.short_description}</p>}
            <div className="flex items-center gap-6 mt-8 text-sm text-white/40">
              <span className="flex items-center gap-1.5"><Headphones className="w-4 h-4 text-[#1A9DA5]" />{(category.products || []).length} guides</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#F0B429]" />Lifetime Access</span>
            </div>
          </div>
        </div>
      </section>

      {/* Long Description */}
      {category.long_description && (
        <section className="py-12 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <p className="text-base leading-relaxed text-muted-foreground whitespace-pre-wrap">{category.long_description}</p>
          </div>
        </section>
      )}

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-16 px-4" data-testid="category-featured">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-5 h-5 text-[#E8735A]" />
              <h2 className="text-2xl font-bold font-['Outfit']">Featured</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProducts.map(p => (
                <Card key={p.id} className="group border-2 border-[#E8735A]/20 hover:border-[#E8735A]/50 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden" onClick={() => navigate(`/${p.type === 'package' ? 'packages' : 'guides'}/${p.slug}`)} data-testid={`featured-product-${p.id}`}>
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-[#E8735A] text-white"><Star className="w-3 h-3 mr-1 fill-white" />Featured</Badge>
                  </div>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-[#E8735A]/10 flex items-center justify-center mb-4">
                      {p.type === 'package' ? <Package className="w-6 h-6 text-[#E8735A]" /> : <Headphones className="w-6 h-6 text-[#E8735A]" />}
                    </div>
                    <h3 className="text-lg font-semibold font-['Outfit'] mb-2 group-hover:text-[#E8735A] transition-colors pr-20">{p.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{p.short_description}</p>
                    {p.outcomes?.length > 0 && (
                      <div className="space-y-1 mb-3">
                        {p.outcomes.slice(0, 2).map((o, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground"><CheckCircle className="w-3 h-3 text-[#1A9DA5] shrink-0" />{o}</div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <PriceDisplay product={p} size="md" />
                      {p.duration_minutes && (
                        <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${durationColor(p.duration_minutes).bg} ${durationColor(p.duration_minutes).text}`}>
                          <Clock className="w-3 h-3" />{p.duration_minutes} min
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Guides */}
      {guides.length > 0 && (
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold font-['Outfit'] mb-6">Audio Guides</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {guides.map(g => (
                <Card key={g.id} className="group hover:border-[#1A9DA5]/50 hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate(`/guides/${g.slug}`)} data-testid={`cat-guide-${g.id}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-[#1A9DA5]/10 flex items-center justify-center mb-4">
                      <Headphones className="w-6 h-6 text-[#1A9DA5]" />
                    </div>
                    <h3 className="text-lg font-semibold font-['Outfit'] mb-2 group-hover:text-[#1A9DA5] transition-colors">{g.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{g.short_description}</p>
                    <div className="flex items-center justify-between">
                      <PriceDisplay product={g} size="md" />
                      {g.duration_minutes && (
                        <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${durationColor(g.duration_minutes).bg} ${durationColor(g.duration_minutes).text}`}>
                          <Clock className="w-3 h-3" />{g.duration_minutes} min
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Packages */}
      {packages.length > 0 && (
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold font-['Outfit'] mb-6">Packages</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {packages.map(p => (
                <Card key={p.id} className="border-2 border-[#1A9DA5]/30 hover:border-[#1A9DA5] hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate(`/packages/${p.slug}`)}>
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl sparky-gradient flex items-center justify-center"><Package className="w-6 h-6 text-white" /></div>
                      {p.savings_display && <Badge className="bg-[#F0B429] text-white">{p.savings_display}</Badge>}
                    </div>
                    <h3 className="text-2xl font-bold font-['Outfit'] mb-2">{p.title}</h3>
                    <p className="text-muted-foreground mb-4">{p.short_description}</p>
                    <PriceDisplay product={p} size="lg" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
